import { useState, useEffect } from 'react';
import { Inbox } from './pages/Inbox.tsx';
import { ItemDetail } from './pages/ItemDetail.tsx';
import { ChartLab } from './pages/ChartLab.tsx';
import { SourceMonitor } from './pages/SourceMonitor.tsx';
import { DigestPreview } from './pages/DigestPreview.tsx';
import { Archive } from './pages/Archive.tsx';
import { Planning } from './pages/Planning.tsx';
import { Tenders } from './pages/Tenders.tsx';

type Page = 'inbox' | 'planning' | 'tenders' | 'charts' | 'sources' | 'digest' | 'archive';

const NAV: { id: Page; label: string }[] = [
  { id: 'inbox',    label: 'Inbox' },
  { id: 'planning', label: 'Planning' },
  { id: 'tenders',  label: 'Tenders' },
  { id: 'charts',   label: 'Chart Lab' },
  { id: 'sources',  label: 'Sources' },
  { id: 'digest',   label: 'Digest' },
  { id: 'archive',  label: 'Archive' },
];

const VALID_PAGES = new Set(NAV.map(n => n.id));

function parseHash(): { page: Page; itemId: string | null } {
  const hash = window.location.hash.replace(/^#\/?/, '');
  const [seg, id] = hash.split('/');
  if (seg === 'items' && id) return { page: 'inbox', itemId: id };
  const page = VALID_PAGES.has(seg as Page) ? (seg as Page) : 'inbox';
  return { page, itemId: null };
}

function setHash(page: Page, itemId?: string | null) {
  const next = itemId ? `#/items/${itemId}` : `#/${page}`;
  if (window.location.hash !== next) {
    window.history.pushState(null, '', next);
  }
}

export default function App() {
  const [page, setPage]           = useState<Page>(() => parseHash().page);
  const [detailItemId, setDetailItemId] = useState<string | null>(() => parseHash().itemId);

  // Sync URL → state on back/forward navigation
  useEffect(() => {
    function onPop() {
      const { page, itemId } = parseHash();
      setPage(page);
      setDetailItemId(itemId);
    }
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  function navTo(p: Page) {
    setDetailItemId(null);
    setPage(p);
    setHash(p);
  }

  function openItem(id: string) {
    setDetailItemId(id);
    setHash(page, id);
  }

  function closeItem() {
    setDetailItemId(null);
    setHash(page);
  }

  return (
    <div style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif', minHeight: '100vh', background: '#f7f7f7' }}>
      {/* Topbar */}
      <div style={{ background: '#111', padding: '0 24px', display: 'flex', alignItems: 'center', height: 52 }}>
        <span style={{ color: '#fff', fontWeight: 700, fontSize: 15, marginRight: 32 }}>
          Business Post&nbsp;<span style={{ color: '#d94f00' }}>Data Intelligence</span>
        </span>
        <nav style={{ display: 'flex', gap: 4 }}>
          {NAV.map(n => (
            <button key={n.id} onClick={() => navTo(n.id)}
              style={{
                background: page === n.id && !detailItemId ? 'rgba(255,255,255,0.15)' : 'transparent',
                color: page === n.id && !detailItemId ? '#fff' : 'rgba(255,255,255,0.6)',
                border: 'none',
                borderRadius: 4,
                padding: '6px 14px',
                fontSize: 13,
                cursor: 'pointer',
                fontWeight: page === n.id && !detailItemId ? 600 : 400,
              }}>
              {n.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Content */}
      <div style={{ maxWidth: 1200, margin: '0 auto', padding: '28px 24px' }}>
        {detailItemId ? (
          <ItemDetail itemId={detailItemId} onBack={closeItem} />
        ) : (
          <>
            {page === 'inbox'    && <Inbox onOpenItem={openItem} />}
            {page === 'planning' && <Planning onOpenItem={openItem} />}
            {page === 'tenders'  && <Tenders onOpenItem={openItem} />}
            {page === 'charts'   && <ChartLab />}
            {page === 'sources'  && <SourceMonitor />}
            {page === 'digest'   && <DigestPreview />}
            {page === 'archive'  && <Archive />}
          </>
        )}
      </div>
    </div>
  );
}
